// My Trading Journey → Who I'm becoming (JourneyIdentity.js): the trader's identity next to what
// their trades say. The habits to drop (Behavior patterns in the last 60 days, with what they cost
// and how many trading days since the last one), the habits to adopt (their rules, and how often
// they were kept), a self-assessment checked against the data, and how their "I am a trader who…"
// statements held up over the last 30 days.
import { PATTERNS, detectBehavior, ruleFor } from './behaviorDetect';
import { netPnl, tradingDayKey } from './pnl';

const DAY = 86400000;
export const IDENTITY_MIN_TRADES = 10;

// The self-assessment: each trait, the patterns that show it in the trades, and how it's measured.
export const TRAITS = [
  { id: 'emotions', label: 'Emotional control', kinds: ['revenge', 'sizeUp', 'streak', 'pastLimit'], measure: 'days' },
  { id: 'patience', label: 'Patience with winners', kinds: ['cutWinners'], measure: 'winners' },
  { id: 'profits', label: 'Protecting my profits', kinds: ['giveBack', 'winSizeUp'], measure: 'days' },
  { id: 'focus', label: 'Focus and selectivity', kinds: ['overtrading', 'offHours', 'unplanned'], measure: 'days' },
  { id: 'risk', label: 'Risk control', measure: 'bigLosses' },
  { id: 'discipline', label: 'Following my rules', measure: 'rules' },
];
export const RATINGS = ['strength', 'ok', 'weakness'];
const rate = (share, strong, fine) => (share <= strong ? 'strength' : share <= fine ? 'ok' : 'weakness');

// What the self-rating and the data say together: they agree, a blind spot (you think it's a
// strength, the trades say weakness), a hidden strength (the other way), or close.
export function compare(self, data) {
  if (!self || !data) return null;
  if (self === data) return 'agree';
  if (self === 'strength' && data === 'weakness') return 'blind';
  if (self === 'weakness' && data === 'strength') return 'hidden';
  return 'close';
}

// `statements`: [{ text, kind }] (kind links a statement to a Behavior pattern); `rules`: the
// trader's rules (Routine). Everything is counted over trading days (days with trades).
export function identityReport(trades, { accounts = [], notes = {}, rules = [], statements = [], today = new Date(), days = 60 } = {}) {
  const since = new Date(today.getTime() - days * DAY);
  const recent = trades.filter((t) => new Date(t.date) >= since && new Date(t.date) <= today);
  if (recent.length < IDENTITY_MIN_TRADES) return { enough: false, count: recent.length };
  const found = detectBehavior(trades, { accounts, notes });
  const kindsOf = (t) => (found.flags.get(t._id) || []).map((f) => f.kind);
  const tradingDays = [...new Set(recent.map((t) => tradingDayKey(t.date)))].sort();

  // Habits to drop: the patterns seen lately, costliest first (quick winners by how often).
  const drop = PATTERNS.map((p) => {
    const hit = recent.filter((t) => kindsOf(t).includes(p.kind));
    if (!hit.length) return null;
    const hitDays = [...new Set(hit.map((t) => tradingDayKey(t.date)))].sort();
    const last = hitDays[hitDays.length - 1];
    return {
      kind: p.kind, name: p.name, count: hit.length, days: hitDays.length, last,
      cost: p.costless ? null : hit.reduce((s, t) => s + netPnl(t), 0),
      clean: tradingDays.filter((d) => d > last).length,
      rule: ruleFor(p.kind, found.baseline),
    };
  }).filter(Boolean)
    .filter((h) => h.cost === null || h.cost < 0)
    .sort((a, b) => ((a.cost === null) - (b.cost === null)) || (a.cost === null ? b.count - a.count : a.cost - b.cost))
    .slice(0, 5);

  // Habits to adopt: each rule, kept on how many of the last 30 days' trading days it was checked.
  const month = tradingDays.filter((d) => new Date(`${d}T12:00:00Z`) >= new Date(today.getTime() - 30 * DAY));
  const checked = month.filter((d) => notes[d] && Array.isArray(notes[d].rulesFollowed) && (notes[d].rulesFollowed.length || notes[d].review || notes[d].score));
  const adopt = rules.map((rule) => ({ rule, kept: checked.filter((d) => notes[d].rulesFollowed.includes(rule)).length, checked: checked.length }));

  // The traits, from the trades.
  const flaggedDays = (kinds) => new Set(recent.filter((t) => kindsOf(t).some((k) => kinds.includes(k))).map((t) => tradingDayKey(t.date))).size;
  const winners = recent.filter((t) => netPnl(t) > 0);
  const losses = recent.filter((t) => netPnl(t) < 0).map((t) => -netPnl(t)).sort((a, b) => a - b);
  const typicalLoss = losses.length ? losses[Math.floor(losses.length / 2)] : 0;
  const allKept = checked.filter((d) => rules.length && rules.every((r) => notes[d].rulesFollowed.includes(r))).length;
  const traits = TRAITS.map((trait) => {
    if (trait.measure === 'days') {
      const n = flaggedDays(trait.kinds);
      return { ...trait, n, total: tradingDays.length, data: rate(n / tradingDays.length, 0.1, 0.25) };
    }
    if (trait.measure === 'winners') {
      const n = winners.filter((t) => kindsOf(t).includes('cutWinners')).length;
      return { ...trait, n, total: winners.length, data: winners.length >= 5 && found.baseline.holds ? rate(n / winners.length, 0.1, 0.3) : null };
    }
    if (trait.measure === 'bigLosses') {
      const n = losses.filter((x) => x > typicalLoss * 2).length;
      return { ...trait, n, total: losses.length, data: losses.length >= 5 ? rate(n / losses.length, 0.05, 0.15) : null };
    }
    return { ...trait, n: allKept, total: checked.length, data: rules.length && checked.length >= 5 ? rate(1 - allKept / checked.length, 0.2, 0.4) : null };
  });

  // Each statement over the last 30 days: trading days without its pattern.
  const checks = statements.map((s) => {
    if (!s.kind) return { ...s, held: null, total: month.length };
    const broke = new Set(recent.filter((t) => month.includes(tradingDayKey(t.date)) && kindsOf(t).includes(s.kind)).map((t) => tradingDayKey(t.date))).size;
    return { ...s, held: month.length - broke, total: month.length };
  });

  return { enough: true, count: recent.length, days: tradingDays.length, drop, adopt, traits, checks };
}

// "I am a trader who…" statements to suggest: one for each habit to drop that isn't one yet.
export const STATEMENTS = {
  revenge: 'waits after a loss before the next trade.',
  sizeUp: 'keeps the same size after a loss.',
  streak: 'stops for the day after 3 losses in a row.',
  pastLimit: 'stops at the daily loss limit, every time.',
  giveBack: 'protects a green day instead of giving it back.',
  winSizeUp: 'keeps normal size after a winning streak.',
  cutWinners: 'lets winners reach the target.',
  unplanned: 'only takes trades from the plan.',
  overtrading: 'takes a few good trades and stops.',
  offHours: 'trades only during the planned hours.',
};
export function suggestions(report, statements) {
  if (!report || !report.enough) return [];
  const taken = new Set(statements.map((s) => s.kind).filter(Boolean));
  return report.drop.filter((h) => STATEMENTS[h.kind] && !taken.has(h.kind)).map((h) => ({ kind: h.kind, text: STATEMENTS[h.kind] }));
}
